if (!customElements.get('payment-plans')) {
  customElements.define(
    'payment-plans',
    class PaymentPlans extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.sectionId = this.dataset.sectionId;
        this.currency = this.dataset.currency || 'EUR';
        this.locale = this.dataset.locale || 'de-DE';
        this.priceCents = Number(this.dataset.priceCents || 0);

        this.country = (this.dataset.country || '').toUpperCase();
        this.config = this.readConfig();
        this.providers = ((this.config && this.config.providers) || []).filter((p) => this.isProviderAvailable(p));
        this.i18n = (this.config && this.config.i18n) || {};

        this.teaserWrap = this.querySelector('[data-ik-pp-teaser]');
        this.teaserOffer = this.querySelector('[data-ik-pp-teaser-offer]');
        this.teaserPlan = this.querySelector('[data-ik-pp-teaser-plan]');
        this.teaserLinkText = this.querySelector('[data-ik-pp-teaser-link-text]');
        this.openBtn = this.querySelector('[data-ik-pp-open]');
        this.plansEl = this.querySelector('[data-ik-pp-plans]');
        this.modalTitle = this.querySelector('[data-ik-pp-modal-title]');
        this.disclaimer = this.querySelector('[data-ik-pp-disclaimer]');
        this.payLaterNote = this.querySelector('[data-ik-pp-pay-later-note]');

        if (this.modalTitle) this.modalTitle.textContent = this.i18n.modal_title || 'See All Flexible Financing Options';
        if (this.disclaimer) this.disclaimer.textContent = this.i18n.disclaimer || '';
        if (this.payLaterNote) {
          this.payLaterNote.textContent =
            this.i18n.pay_later_note ||
            'Pay Later is available in supported countries. Availability, payment plans, and eligibility may vary by region and account.';
        }
        if (this.teaserLinkText) {
          this.teaserLinkText.textContent = this.i18n.view_options || 'See all flexible financing options';
        }

        if (this.openBtn) {
          this.openBtn.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.openBtn.click();
            }
          });
        }

        this.render();

        this.variantUnsub = null;
        if (window.FoxTheme && FoxTheme.pubsub) {
          this.variantUnsub = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
            const variant = event.data.variant;
            if (!variant || typeof variant.price !== 'number') return;
            this.priceCents = variant.price;
            this.dataset.priceCents = String(variant.price);
            this.render();
          });
        }
      }

      disconnectedCallback() {
        if (this.variantUnsub) {
          this.variantUnsub();
          this.variantUnsub = null;
        }
        this._init = false;
      }

      readConfig() {
        const node = this.querySelector('[data-payment-plans-config]');
        if (!node) return { providers: [], i18n: {} };
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          console.warn('[payment-plans] invalid config', e);
          return { providers: [], i18n: {} };
        }
      }

      /** Provider must be enabled and allow the shopper's country. */
      isProviderAvailable(provider) {
        if (!provider || !provider.enabled) return false;
        const list = this.normalizeCountries(provider.countries);
        if (!list.length) return false;
        return list.indexOf(this.country) !== -1;
      }

      normalizeCountries(value) {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean);
        }
        return String(value)
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
      }

      formatMoney(amount, fractionDigits = 2) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          }).format(amount);
        } catch (e) {
          return amount.toFixed(fractionDigits) + ' ' + this.currency;
        }
      }

      round2(n) {
        return Math.round((n + Number.EPSILON) * 100) / 100;
      }

      ceil2(n) {
        return Math.ceil((n - Number.EPSILON) * 100) / 100;
      }

      getTermsForPrice(provider, price) {
        if (!provider || !Array.isArray(provider.tiers)) return [];
        const sorted = provider.tiers
          .slice()
          .filter((t) => typeof t.minPrice === 'number')
          .sort((a, b) => a.minPrice - b.minPrice);

        let terms = [];
        for (const tier of sorted) {
          if (price >= tier.minPrice) {
            terms = Array.isArray(tier.terms) ? tier.terms.slice() : [];
          }
        }
        return terms;
      }

      buildInstallmentPlan(provider, price, term) {
        const rate = Number((provider.rates && provider.rates[String(term)]) || 0);
        const interest = this.round2(price * rate);
        const total = this.round2(price + interest);
        const monthly = provider.roundMode === 'round' ? this.round2(total / term) : this.ceil2(total / term);
        return {
          providerId: provider.id,
          ui: provider.ui,
          type: 'installment',
          term,
          monthly,
          monthlyDisplay: monthly,
          interest,
          interestDisplay: interest,
          total,
          totalDisplay: total,
          price,
        };
      }

      buildPayLaterPlan(provider, price) {
        return {
          providerId: provider.id,
          ui: provider.ui,
          type: 'pay_later',
          term: 0,
          monthly: null,
          monthlyDisplay: this.round2(price),
          interest: 0,
          interestDisplay: 0,
          total: this.round2(price),
          totalDisplay: this.round2(price),
          price: this.round2(price),
        };
      }

      buildProviderPlans(provider, price) {
        const minInstallments = Number(provider.minInstallments || 6);
        const terms = this.getTermsForPrice(provider, price).filter((t) => t >= minInstallments);
        const plans = terms.map((term) => this.buildInstallmentPlan(provider, price, term));

        const extras = provider.alwaysInclude || [];
        if (extras.indexOf('pay_later') !== -1) {
          // Klarna Pay later in 30 days is always available when Klarna is shown.
          // If installments are not unlocked yet, still offer the 30-day plan alone.
          plans.push(this.buildPayLaterPlan(provider, price));
        }

        return plans;
      }

      pickLowestMonthly(plans) {
        const installments = (plans || []).filter((p) => p && p.type === 'installment' && typeof p.monthly === 'number');
        if (!installments.length) return null;
        return installments.reduce((best, plan) => (plan.monthly < best.monthly ? plan : best));
      }

      interpolate(template, vars) {
        return String(template || '')
          .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (vars[key] != null ? String(vars[key]) : ''))
          .replace(/\[\[(\w+)\]\]/g, (_, key) => (vars[key] != null ? String(vars[key]) : ''));
      }

      providerLabel(id) {
        return this.i18n['provider_' + id] || id;
      }

      brandMarkup(provider) {
        const id = typeof provider === 'string' ? provider : provider && provider.id;
        const logo =
          (provider && provider.logo) ||
          (id === 'paypal'
            ? 'https://cdn.shopify.com/s/files/1/0893/4706/4137/files/paypal-logo-pc.svg?v=1785809061'
            : 'https://cdn.shopify.com/s/files/1/0893/4706/4137/files/klarna-logo-pc.svg?v=1785809061');
        const label = this.escape(this.providerLabel(id));
        return `<img class="ik-pp__provider-logo ik-pp__provider-logo--${this.escape(id || 'klarna')}" src="${this.escape(
          logo
        )}" alt="${label}" width="96" height="28" loading="lazy" decoding="async">`;
      }

      renderTeaser(best) {
        if (!this.teaserWrap || !best) {
          if (this.teaserWrap) this.teaserWrap.hidden = true;
          return;
        }
        this.teaserWrap.hidden = false;
        const amount = this.formatMoney(best.monthlyDisplay, 2);
        if (this.teaserOffer) {
          this.teaserOffer.textContent = this.interpolate(
            this.i18n.teaser_line || 'or [[amount]] /mo for [[months]] months',
            { amount, months: best.term }
          );
        }
        if (this.teaserPlan) {
          this.teaserPlan.textContent = this.interpolate(this.i18n.current_plan || 'Current Plan: [[provider]]', {
            provider: this.providerLabel(best.providerId),
          });
        }
        if (this.openBtn) {
          this.openBtn.setAttribute('aria-label', this.i18n.open_details || this.i18n.view_options || '');
        }
      }

      renderPlanList(plans) {
        const interestLabel = this.i18n.interest_label || 'Interest';
        const totalLabel = this.i18n.total_of_payments || 'Total of Payments';
        const everyMonth = this.i18n.every_month || 'every month';
        const payLaterLabel = this.i18n.pay_later || 'Pay later';
        const in30Days = this.i18n.in_30_days || 'in 30 days';

        const ordered = plans.slice().sort((a, b) => {
          // Pay later always last; installments by monthly fee low → high (usually longer terms first).
          if (a.type === 'pay_later' && b.type !== 'pay_later') return 1;
          if (b.type === 'pay_later' && a.type !== 'pay_later') return -1;
          const ma = typeof a.monthly === 'number' ? a.monthly : Number.POSITIVE_INFINITY;
          const mb = typeof b.monthly === 'number' ? b.monthly : Number.POSITIVE_INFINITY;
          if (ma !== mb) return ma - mb;
          return (b.term || 0) - (a.term || 0);
        });

        return ordered
          .map((plan) => {
            const isPayLater = plan.type === 'pay_later';
            const badge = isPayLater
              ? payLaterLabel
              : this.interpolate(this.i18n.n_months || '[[count]] months', { count: plan.term });
            const amount = this.formatMoney(plan.monthlyDisplay, 2);
            const schedule = isPayLater ? in30Days : everyMonth;
            const interest = this.formatMoney(plan.interestDisplay, 2);
            const total = this.formatMoney(plan.totalDisplay, 2);
            return `
            <article class="ik-pp__plan${isPayLater ? ' ik-pp__plan--pay-later' : ''}" role="listitem">
              <div class="ik-pp__plan-top">
                <p class="ik-pp__plan-monthly">
                  <span class="ik-pp__plan-amount">${this.escape(amount)}</span>
                  <span class="ik-pp__plan-every">${this.escape(schedule)}</span>
                </p>
                <span class="ik-pp__plan-badge">${this.escape(badge)}</span>
              </div>
              <div class="ik-pp__plan-rows">
                <div class="ik-pp__plan-row">
                  <span>${this.escape(interestLabel)}</span>
                  <span>${this.escape(interest)}</span>
                </div>
                <div class="ik-pp__plan-row">
                  <span>${this.escape(totalLabel)}</span>
                  <span>${this.escape(total)}</span>
                </div>
              </div>
            </article>
          `;
          })
          .join('');
      }

      render() {
        if (!this.providers.length) {
          this.hidden = true;
          return;
        }

        const price = this.round2(this.priceCents / 100);
        const sections = [];
        const allPlans = [];

        for (const provider of this.providers) {
          const plans = this.buildProviderPlans(provider, price);
          if (!plans.length) continue;
          // Teaser only cares about multi-month installments (lowest monthly).
          allPlans.push(...plans.filter((p) => p.type === 'installment'));
          sections.push({ provider, plans });
        }

        if (!sections.length) {
          this.hidden = true;
          if (this.teaserWrap) this.teaserWrap.hidden = true;
          if (this.plansEl) this.plansEl.innerHTML = '';
          return;
        }

        this.hidden = false;
        const best = this.pickLowestMonthly(allPlans);
        // If only pay-later options exist (no multi-month installments), still show teaser opener.
        if (best) {
          this.renderTeaser(best);
        } else if (this.teaserWrap) {
          this.teaserWrap.hidden = false;
          if (this.teaserOffer) {
            this.teaserOffer.textContent = this.i18n.view_options || 'See all flexible financing options';
          }
          if (this.teaserPlan) {
            this.teaserPlan.textContent = this.interpolate(this.i18n.current_plan || 'Current Plan: [[provider]]', {
              provider: this.providerLabel(sections[0].provider.id),
            });
          }
        }

        // Mark the provider that offers the overall lowest monthly as Recommended.
        const recommendedId = best ? best.providerId : sections[0].provider.id;
        const checkoutNote = this.i18n.checkout_note || 'Process at the checkout';
        const recommendedLabel = this.i18n.recommended || 'Recommended';

        if (this.plansEl) {
          this.plansEl.innerHTML = sections
            .map(({ provider, plans }) => {
              const isRecommended = provider.id === recommendedId;
              return `
              <section class="ik-pp__card ik-pp__card--${this.escape(provider.id)}">
                <div class="ik-pp__card-head">
                  <div class="ik-pp__card-brand">
                    ${this.brandMarkup(provider)}
                    <p class="ik-pp__card-note">${this.escape(checkoutNote)}</p>
                  </div>
                  ${
                    isRecommended
                      ? `<span class="ik-pp__recommended">${this.escape(recommendedLabel)}</span>`
                      : ''
                  }
                </div>
                <div class="ik-pp__card-plans" role="list">
                  ${this.renderPlanList(plans)}
                </div>
              </section>
            `;
            })
            .join('');
        }
      }

      escape(str) {
        return String(str || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }
    }
  );
}
